import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { CardPackCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { GlareEffect } from '@sorare/core/src/atoms/animations/GlareEffect';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper/index.tsx';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandXXS } from '@sorare/core/src/atoms/typography';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { ANY_SPORT_SERIES_MARKET_COMMON_PACKS_GROUP } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { CARD_SIZE } from '@sorare/core/src/lib/cards';
import type { Unit } from '@sorare/core/src/lib/style';
import { mobileAndAbove } from '@sorare/core/src/style/mediaQuery';

import {
  BuyPackButton,
  getDiscountPercentage,
} from '#src/components/commonCard/BuyPackButton/index.tsx';
import { EligiblePlayers } from '#src/components/commonCard/BuyPacks/EligiblePlayers/index.tsx';
import { PackBadge } from '#src/components/commonCard/BuyPacks/PackWithBuyButton/PackBadge/index.tsx';
import { PackDescription } from '#src/components/commonCard/BuyPacks/PackWithBuyButton/PackDescription/index.tsx';

import type { PackWithBuyButton_cardPack } from './__generated__/index.graphql.ts';

const PACK_IMAGE_WIDTH = 120;

const HorizontalRoot = styled(Horizontal)`
  position: relative;
  width: 100%;
  align-items: flex-start;
  padding: 0 calc(${PACK_IMAGE_WIDTH}px + var(--quadruple-unit)) 0
    var(--intermediate-unit);
  overflow: hidden;
  isolation: isolate;
  margin-bottom: calc(-2 * var(--unit));
`;

const HorizontalContent = styled(Vertical).attrs({ gap: 1.5 })`
  flex: 1;
  align-items: flex-start;
  padding-bottom: var(--double-unit);
`;

const HorizontalPackImageLink = styled(Link)`
  position: absolute;
  right: var(--intermediate-unit);
  bottom: calc(-1 * var(--triple-unit));
  width: ${PACK_IMAGE_WIDTH}px;

  &.disabled {
    opacity: 0.4;
    pointer-events: none;
  }
`;

const HorizontalPackImage = styled.img`
  width: 100%;
  object-fit: contain;
`;

const Root = styled(Vertical)`
  position: relative;
  isolation: isolate;
  will-change: transform;
  justify-content: space-between;
  transition: transform 0.3s ease-in-out;
  &.scaleOnHover:hover,
  &.scaleOnHover:focus {
    transform: scale(1.02);
  }
`;

const PackImageWrapper = styled(Horizontal).attrs({ center: true })`
  position: relative;
  isolation: isolate;
`;

const PositionnedEligiblePlayers = styled.div`
  position: absolute;
  bottom: calc(-1 * var(--unit));
  right: calc(-1 * var(--quadruple-unit));
  left: calc(-1 * var(--quadruple-unit));
`;

const PackImg = styled(GlareEffect)`
  position: relative;
  object-fit: contain;
  height: 100%;
  width: calc(12 * var(--unit));
  @media ${mobileAndAbove} {
    width: calc(16 * var(--unit));
  }
  &.disabled {
    opacity: 0.5;
  }
`;

const Badge = styled(BrandXXS)`
  background: var(--c-nd-100);
  color: var(--c-white);
  padding: 2px var(--half-unit);
  border-radius: var(--half-unit);
`;

const ExtraLabelBadge = styled(Badge)`
  position: absolute;
  top: var(--unit);
  right: calc(-1 * var(--unit));
  color: var(--c-black);
  background: var(--c-gradient-conversionCreditBorder);
  &.gold {
    background: var(--c-gradient-gold);
  }
`;

type Props = {
  cardPack: PackWithBuyButton_cardPack;
  cardPackGroupSlug: string;
  badge?: ReactNode;
  extraContent?: ReactNode;
  gap?: Unit;
  hideRemainingPurchases?: boolean;
  isPremiumPack?: boolean;
  scaleOnHover?: boolean;
  withEligiblePlayers?: boolean;
  variant?: 'horizontal' | 'vertical';
  onCardsClaimed?: () => void;
};
export const PackWithBuyButton = ({
  cardPack,
  cardPackGroupSlug,
  badge: badgeOverride,
  extraContent,
  gap = 1,
  hideRemainingPurchases = false,
  isPremiumPack = false,
  scaleOnHover = true,
  withEligiblePlayers = false,
  variant = 'vertical',
  onCardsClaimed,
}: Props) => {
  const { generateSportContextPath } = useSportContext();

  const isGemsPack = cardPack?.currency === CardPackCurrency.COMMON_GEM;
  const commonPlayers = cardPack?.eligibleCommonPlayers;
  const isLaptopAndAbove = useIsLaptop();
  const { canPurchase, extraLabelDescription } = cardPack;
  const packPath = `${generateSportContextPath(
    ANY_SPORT_SERIES_MARKET_COMMON_PACKS_GROUP,
    {
      params: {
        group: cardPackGroupSlug,
      },
    }
  )}?pack=${cardPack.slug}`;
  const discountPercentage = getDiscountPercentage(
    cardPack.discountedPrice,
    cardPack.price
  );
  const buyButton = (
    <BuyPackButton
      cardPack={cardPack}
      size="small"
      color={isGemsPack ? 'primary' : 'secondary'}
      confirmPurchaseColor={isGemsPack ? 'secondary' : 'primary'}
      hideRemainingPurchases={hideRemainingPurchases}
      onCardsClaimed={onCardsClaimed}
    />
  );

  const title = !!cardPack.title && (
    <BrandM as="h3" color="var(--c-white)">
      {cardPack.title}
    </BrandM>
  );
  const badge = badgeOverride ?? <PackBadge cardPack={cardPack} />;
  const description = <PackDescription cardPack={cardPack} />;

  const extraLabelBadge =
    (discountPercentage > 0 && (
      <ExtraLabelBadge className="gold">
        <FormattedMessage
          defaultMessage="-{discount}% discount"
          values={{ discount: discountPercentage }}
        />
      </ExtraLabelBadge>
    )) ||
    (extraLabelDescription && (
      <ExtraLabelBadge>{extraLabelDescription}</ExtraLabelBadge>
    ));
  if (variant === 'horizontal') {
    return (
      <>
        <HorizontalRoot>
          <HorizontalContent>
            {badge}
            {title}
            {description}
            {extraContent}
            <BuyPackButton
              cardPack={cardPack}
              size="small"
              color="primary"
              confirmPurchaseColor="secondary"
              hideRemainingPurchases={hideRemainingPurchases}
              onCardsClaimed={onCardsClaimed}
            />
          </HorizontalContent>
          {!!cardPack.pictureUrl && (
            <HorizontalPackImageLink
              to={packPath}
              className={classNames({ disabled: !canPurchase })}
            >
              <HorizontalPackImage src={cardPack.pictureUrl} alt="" />
            </HorizontalPackImageLink>
          )}
        </HorizontalRoot>
        {extraLabelBadge}
      </>
    );
  }

  const displayDetails = isLaptopAndAbove;

  return (
    <>
      <Root
        center
        gap={gap}
        className={classNames({
          scaleOnHover,
          isPremiumPack,
        })}
      >
        <PackImageWrapper>
          <ConditionalWrapper
            wrap={!!cardPackGroupSlug}
            Wrapper={Link}
            props={{ to: packPath }}
          >
            <PackImg
              pictureUrl={proxyUrl(cardPack.pictureUrl, {
                cropWidth: CARD_SIZE,
              })}
              disabled={!canPurchase}
              className={classNames({ disabled: !canPurchase })}
            />
            {!!withEligiblePlayers && commonPlayers?.length > 0 && (
              <PositionnedEligiblePlayers>
                <EligiblePlayers
                  commonPlayers={commonPlayers}
                  color={cardPack?.primaryColor}
                />
              </PositionnedEligiblePlayers>
            )}
          </ConditionalWrapper>
        </PackImageWrapper>
        {!!displayDetails && (
          <Vertical gap={0.5} center className="text-center">
            {badge}
            {title}
            {description}
            {extraContent}
          </Vertical>
        )}
        {buyButton}
      </Root>
      {extraLabelBadge}
    </>
  );
};

PackWithBuyButton.fragments = {
  cardPack: gql`
    fragment PackWithBuyButton_cardPack on CardPack {
      slug
      title
      discountedPrice
      price
      canPurchase
      pictureUrl
      extraLabelDescription
      primaryColor: color(variant: PRIMARY)
      currency
      eligibleCommonPlayers {
        id
        ...EligiblePlayers_commonPlayer
      }
      ...BuyPackButton_cardPack
      ...PackBadge_cardPack
      ...PackDescription_cardPack
    }
    ${EligiblePlayers.fragments.commonPlayer}
    ${PackBadge.fragments.cardPack}
    ${PackDescription.fragments.cardPack}
    ${BuyPackButton.fragments.cardPack}
  ` as TypedDocumentNode<PackWithBuyButton_cardPack>,
};
